/** Side panel GUI — mirrors assistant state; submits through the same input dispatch. */
import { esc, fmtAmt, leadOutstandingBalance, STATUS_LABELS, canDeleteLead, isViewer } from './utils.js';
import { statusDisplayName } from './assistant-stages.js';
import { isSkip } from './assistant-ticket-new.js';
import {
  listPassengerTargets,
  passengerRemoveActionLabel,
} from './assistant-edit.js';
import { pricingMenuText, listPricingFieldChoices, listPricingTargets, pricingValuePrompt } from './assistant-pricing.js';
import { formatLeadBriefRich } from './assistant-lead-format.js';
import { describeSearchFilters, searchPageLabel } from './assistant-search.js';

function isYnStepKey(key) {
  if (!key) return false;
  if (key === 'passenger_summary' || key === 'seats_booked') return true;
  if (key.endsWith(':same_itinerary') || key.endsWith(':same_fare')) return true;
  if (key.endsWith(':fare_confirm') || key.endsWith(':confirm')) return true;
  return false;
}

const DATE_STEP_HINT = 'YYMMDD or YYYY-MM-DD';
const DATETIME_STEP_HINT = 'YYMMDD HHMM or YYYY-MM-DD HH:MM';

function isDateTimeStepKey(key) {
  const k = String(key || '');
  return k === 'booking_expires' || k === 'pay_before' || k === 'remain_pay_by'
    || k === 'payment_date' || k.endsWith('_expires') || k.endsWith('_by');
}

function isDateOnlyStepKey(key) {
  const k = String(key || '');
  if (k === 'quote_valid_till') return true;
  if (isDateTimeStepKey(k)) return false;
  return k.includes('date') || k.endsWith('_on') || k === 'date_from' || k === 'date_to'
    || k === 'travel_date' || k === 'advance_date' || k === 'balance_collect_date'
    || k === 'followup_date' || k === 'visa_applied_date' || k === 'ticket_booked_on'
    || k === 'visa_issued_date' || k === 'issued_date';
}

function isAmountStepKey(key) {
  const k = String(key || '');
  return k.includes('amount')
    || k.includes('total')
    || k === 'budget'
    || k === 'advance'
    || k === 'seat_fee'
    || k.startsWith('rate_')
    || k.startsWith('sell_')
    || k.endsWith('_total')
    || k === 'final_quote'
    || k === 'agent_total'
    || k === 'quote_price'
    || k === 'payment_amount';
}

function getManageStepGuiView(step, question, def) {
  const title = question.split('\n')[0];
  const hint = step.hint || (def != null && def !== '' ? `Current: ${def}` : null);
  const base = {
    optional: !!step.optional,
    keepValue: def != null && def !== '' ? String(def) : null,
  };

  if (step.key === '_intro') {
    return {
      kind: 'choices',
      title,
      subtitle: step.hint || 'Information only — continue when ready.',
      choices: [{ label: 'Continue', value: '', primary: true }],
    };
  }

  if (step.confirm || isYnStepKey(step.key)) {
    return { kind: 'yn', title, subtitle: hint, yes: 'Y', no: 'N' };
  }

  if (step.key === 'call_via') {
    return {
      kind: 'choices',
      title,
      hint: step.hint || null,
      choices: [
        { label: 'WhatsApp', value: '1' },
        { label: 'Phone', value: '2' },
      ],
    };
  }

  if (step.key === 'reason') {
    return {
      kind: 'choices',
      title,
      hint: step.hint || null,
      choices: [
        { label: 'Declined quote', value: '1' },
        { label: 'Paused — may contact later', value: '2' },
        { label: 'Other', value: '3' },
      ],
    };
  }

  if (isDateTimeStepKey(step.key)) {
    return {
      kind: 'text',
      title,
      hint: hint || DATETIME_STEP_HINT,
      inputType: 'text',
      placeholder: '260725 1745',
      ...base,
    };
  }

  if (isDateOnlyStepKey(step.key)) {
    return {
      kind: 'text',
      title,
      hint: hint || DATE_STEP_HINT,
      inputType: 'text',
      placeholder: '260725',
      ...base,
    };
  }

  if (isAmountStepKey(step.key)) {
    return {
      kind: 'text',
      title,
      hint: hint || 'Amount in LKR',
      inputType: 'number',
      placeholder: 'Amount',
      ...base,
    };
  }

  if (step.key === 'requirements' || step.key === 'ticket_details' || step.key === 'note') {
    return {
      kind: 'text',
      title,
      hint,
      inputType: 'text',
      placeholder: step.hint || 'Details',
      ...base,
    };
  }

  return {
    kind: 'text',
    title,
    hint,
    inputType: inferInputType(step.key),
    ...base,
  };
}

const EDIT_MENU = [
  { label: 'Contact name', value: '1' },
  { label: 'Phone', value: '2' },
  { label: 'Note', value: '3' },
  { label: 'PNR', value: '4' },
  { label: 'Source', value: '5' },
  { label: 'Airline', value: '6' },
  { label: 'Passengers / pricing', value: '7' },
  { label: 'Client not interested', value: '8', danger: true },
  { label: 'Done', value: 'done', primary: false },
];

/**
 * @param {object} state
 * @param {object} ctx
 * @returns {object|null}
 */
export function getAssistantGuiView(state, ctx) {
  if (state.saving) {
    return { kind: 'busy', title: 'Please wait…', subtitle: 'Saving in progress' };
  }

  if (state.mode === 'menu') {
    return {
      kind: 'menu',
      title: 'What do you want to do?',
      actions: [
        { label: 'New ticket', value: 'new ticket', primary: true },
        { label: 'New visa', value: 'new visa' },
        { label: 'New tour', value: 'new tour' },
        { label: 'Stats', value: 'stats' },
        { label: 'Help', value: 'help' },
        { label: 'Clear screen', value: 'clear' },
      ],
      search: true,
      searchHint: 'Search: /text john · Filters: /tickets /visa /tour /mine /madush',
    };
  }

  if (state.mode === 'search_choice') {
    return {
      kind: 'choices',
      title: 'How do you want to search?',
      subtitle: state.pendingSearchQuery ? `Query: ${state.pendingSearchQuery}` : null,
      choices: [
        { label: '1 — Terminal search', value: '1', primary: true },
        { label: '2 — Split screen (leads)', value: '2' },
        { label: 'Cancel', value: 'ig', muted: true },
      ],
    };
  }

  if (state.mode === 'search') {
    const results = state.searchResults || [];
    const pageInfo = searchPageLabel(state.searchPage, state.searchTotalPages, state.searchTotal);
    return {
      kind: 'search',
      title: pageInfo,
      subtitle: state.searchParsed ? describeSearchFilters(state.searchParsed) : null,
      items: results.map((lead, i) => ({
        index: i + 1,
        label: formatLeadBriefRich(lead),
        selected: state.searchPick === i,
        due: leadOutstandingBalance(lead),
        status: STATUS_LABELS[lead.status] || lead.status,
      })),
      actions: [
        ...(state.searchPage > 1 ? [{ label: 'MU — Prev page', value: 'MU' }] : []),
        ...(state.searchPage < state.searchTotalPages ? [{ label: 'MD — Next page', value: 'MD' }] : []),
        { label: 'Back to menu', value: 'ig' },
      ],
    };
  }

  if (state.mode === 'confirm') {
    return {
      kind: 'yn',
      title: 'Save this inquiry?',
      subtitle: 'Review the summary in the terminal.',
      yes: 'Y',
      no: 'N',
    };
  }

  if (state.mode === 'post_save') {
    return {
      kind: 'choices',
      title: 'Inquiry saved',
      subtitle: 'Continue working on it or return to the menu?',
      choices: [
        { label: '1 — Keep editing', value: '1', primary: true },
        { label: '2 — Cancel (menu)', value: '2', muted: true },
      ],
    };
  }

  if (state.mode === 'ticket') {
    const step = ctx.currentNewTicketStep?.();
    const captureLabels = { ticket: 'Ticket', visa: 'Visa', tour: 'Tour' };
    const captureLabel = captureLabels[state.captureType] || 'Inquiry';
    if (!step) return { kind: 'idle', title: `${captureLabel} capture`, subtitle: 'Follow the terminal.' };

    const question = typeof step.ask === 'function' ? step.ask(state.data) : step.ask;
    const title = question.split('\n')[0];

    if (step.key === '_intro') {
      return {
        kind: 'choices',
        title,
        subtitle: step.hint || 'Information only — continue when ready.',
        choices: [{ label: 'Continue', value: '', primary: true }],
      };
    }

    if (step.confirm || isYnStepKey(step.key)) {
      return { kind: 'yn', title, subtitle: step.hint || null, yes: 'Y', no: 'N' };
    }

    if (step.key.endsWith(':gender')) {
      return {
        kind: 'choices',
        title,
        choices: [
          { label: 'Male (M)', value: 'M' },
          { label: 'Female (F)', value: 'F' },
        ],
      };
    }

    if (step.key === 'ticket_class') {
      return {
        kind: 'choices',
        title: 'Travel class',
        subtitle: step.hint || null,
        choices: [
          { label: 'Economy', value: 'economy' },
          { label: 'Business', value: 'business' },
          { label: 'First', value: 'first' },
          { label: 'Skip', value: 'skip', muted: true },
        ],
      };
    }

    if (step.key === 'add_passenger_type') {
      return {
        kind: 'choices',
        title: 'Passenger type',
        choices: [
          { label: 'Adult (ADT)', value: 'ADT' },
          { label: 'Student (STU)', value: 'STU' },
          { label: 'Child (CHD)', value: 'CHD' },
          { label: 'Infant (INF)', value: 'INF' },
        ],
      };
    }

    if (step.key === 'source') {
      return {
        kind: 'choices',
        title: 'Lead source',
        choices: (state.sources || []).map((s) => ({ label: s, value: s })),
      };
    }

    if (step.key === 'package' && state.captureType === 'visa') {
      const pkgs = state.data?._packages?.length
        ? state.data._packages
        : (state.visaPackages?.length ? state.visaPackages : ['India eVisa', 'Malaysia eVisa', 'Vietnam eVisa', 'Dubai Visa']);
      return {
        kind: 'choices',
        title: 'Visa package',
        choices: pkgs.slice(0, 8).map((p, i) => ({ label: p, value: String(i + 1) })),
      };
    }

    if (step.key === 'india_purpose' && state.captureType === 'visa') {
      return {
        kind: 'choices',
        title: 'India eVisa purpose',
        choices: [
          { label: '1 — Tourist', value: '1' },
          { label: '2 — Business', value: '2' },
          { label: '3 — Other', value: '3' },
        ],
      };
    }

    if (step.key === 'india_duration' && state.captureType === 'visa') {
      return {
        kind: 'choices',
        title: 'India eVisa duration',
        choices: [
          { label: '1 — 30 days', value: '1' },
          { label: '2 — 1 year', value: '2' },
        ],
      };
    }

    if ((step.key === 'cost_adt' || step.key === 'cost_chd' || step.key === 'sell_adt' || step.key === 'sell_chd') && state.captureType === 'visa') {
      return {
        kind: 'text',
        title: question,
        hint: step.hint || null,
        inputType: 'number',
        placeholder: 'LKR amount',
      };
    }

    if (step.key === 'package' && state.captureType === 'tour') {
      const pkgs = state.data?._packages?.length
        ? state.data._packages
        : (state.tourPackages?.length ? state.tourPackages : ['Vietnam', 'India', 'Dubai', 'Bangkok']);
      return {
        kind: 'choices',
        title: 'Tour package',
        choices: pkgs.slice(0, 8).map((p, i) => ({ label: p, value: String(i + 1) })),
      };
    }

    if (step.key === 'pref_transit') {
      return {
        kind: 'choices',
        title: 'Flight routing',
        choices: [
          { label: '1 — Direct', value: '1' },
          { label: '2 — Transit', value: '2' },
          { label: '3 — Multi-City', value: '3' },
        ],
      };
    }

    if (step.key === 'pref_trip_type' && state.captureType === 'ticket') {
      return {
        kind: 'choices',
        title: 'Trip type',
        choices: [
          { label: '1 — One way', value: '1' },
          { label: '2 — Return', value: '2' },
        ],
      };
    }

    if (step.key === 'pref_transit_via' && state.captureType === 'ticket') {
      return {
        kind: 'text',
        title: question,
        hint: step.hint || '3-letter IATA code',
        inputType: 'text',
        placeholder: 'e.g. DOH',
      };
    }

    if (step.key === 'pref_multi_city' && state.captureType === 'ticket') {
      return {
        kind: 'text',
        title: question,
        hint: step.hint || 'e.g. CMB SIN KUL CMB',
        inputType: 'text',
        placeholder: 'CMB SIN KUL CMB',
      };
    }

    if (step.key === 'pref_baggage') {
      return {
        kind: 'choices',
        title: 'Baggage preference',
        choices: [
          { label: 'Cabin only', value: '1' },
          { label: 'Checked 23kg', value: '2' },
          { label: 'Checked 30kg+', value: '3' },
          { label: 'Extra baggage', value: '4' },
          { label: 'Skip', value: 'skip', muted: true },
        ],
      };
    }

    if (step.key === 'pref_seat') {
      return {
        kind: 'choices',
        title: 'Seat preference',
        choices: [
          { label: 'Window', value: '1' },
          { label: 'Aisle', value: '2' },
          { label: 'Middle', value: '3' },
          { label: 'Exit row', value: '4' },
          { label: 'Front', value: '5' },
          { label: 'Skip', value: 'skip', muted: true },
        ],
      };
    }

    if (step.key.startsWith('pax_') && state.captureType === 'ticket') {
      return {
        kind: 'text',
        title,
        hint: step.key === 'pax_adt' ? (step.hint || '0 if students only') : (step.hint || null),
        optional: !!step.optional,
        inputType: 'number',
        placeholder: '0',
      };
    }

    const inputType = inferInputType(step.key);
    return {
      kind: 'text',
      title,
      hint: step.hint || null,
      optional: !!step.optional,
      inputType,
      placeholder: step.hint || '',
    };
  }

  if (state.mode === 'manage') {
    if (state.managePhase === 'stage_review') {
      return {
        kind: 'choices',
        title: 'Proceed to next stage?',
        subtitle: ctx.nextStatus?.() ? statusDisplayName(ctx.nextStatus()) : null,
        choices: [
          { label: 'Yes — proceed', value: 'Y', primary: true },
          { label: 'No — stay', value: 'N' },
          { label: 'Edit stage details', value: 'edit' },
        ],
      };
    }

    if (state.managePhase === 'inquiry_options' || state.managePhase === 'complete_menu') {
      const balanceDue = ctx.tourHasBalanceDue?.() ?? (state.lead?.type === 'tour' && leadOutstandingBalance(state.lead) > 0);
      return {
        kind: 'choices',
        title: 'Inquiry actions',
        choices: [
          { label: 'Summary', value: 'summary' },
          { label: 'History', value: 'history' },
          { label: 'Edit stage details', value: 'edit' },
          { label: 'Pricing', value: 'pricing' },
          { label: 'Log call', value: 'call' },
          ...(balanceDue ? [{ label: 'Collect balance', value: 'collect balance', primary: true }] : []),
          { label: 'Exit to menu', value: 'ig', muted: true },
        ],
      };
    }

    if (state.managePhase === 'advance') {
      const next = ctx.nextStatus?.();
      return {
        kind: 'yn',
        title: next ? `Move to ${statusDisplayName(next)}?` : 'Advance stage?',
        yes: 'Y',
        no: 'N',
      };
    }

    if (state.managePhase === 'delete_confirm') {
      const label = state.lead?.name || state.lead?.package_name || 'this inquiry';
      return {
        kind: 'yn',
        title: `Delete ${label}?`,
        subtitle: 'Moves to recycle bin.',
        yes: 'Y',
        no: 'N',
      };
    }

    if (state.managePhase === 'edit_pax_remove_confirm' && state.editPaxTarget) {
      const action = passengerRemoveActionLabel(state.editPaxTarget, state.lead);
      return {
        kind: 'yn',
        title: `${action}?`,
        subtitle: state.editPaxTarget.label,
        yes: 'Y',
        no: 'N',
      };
    }

    if (state.managePhase === 'edit_menu') {
      return { kind: 'choices', title: 'Edit field', choices: EDIT_MENU };
    }

    if (state.managePhase === 'edit_pax_pick') {
      const targets = listPassengerTargets(state.lead);
      return {
        kind: 'choices',
        title: 'Edit passenger',
        choices: [
          ...targets.map((t, i) => ({ label: t.label, value: String(i + 1) })),
          { label: 'Back', value: 'back', muted: true },
        ],
      };
    }

    if (state.managePhase === 'edit_pax_field_menu' && state.editPaxTarget) {
      const choices = ctx.passengerFieldChoices?.(state.editPaxTarget, state.lead) || [];
      const removeErr = ctx.canRemovePassengerTarget?.(state.lead, state.editPaxTarget);
      return {
        kind: 'choices',
        title: `Edit ${state.editPaxTarget.label}`,
        choices: [
          ...choices,
          ...(removeErr ? [] : [{ label: passengerRemoveActionLabel(state.editPaxTarget, state.lead), value: 'remove', danger: true }]),
          { label: 'Back', value: 'back', muted: true },
        ],
      };
    }

    if (state.managePhase === 'due_block') {
      const due = leadOutstandingBalance(state.lead);
      return {
        kind: 'text',
        title: 'Balance due',
        subtitle: due ? `Due: ${fmtAmt(due)}` : null,
        hint: 'Enter amount (LKR) or pay date YYMMDD',
        inputType: 'text',
        placeholder: 'Amount or YYMMDD',
      };
    }

    if (state.managePhase === 'tour_balance') {
      const due = leadOutstandingBalance(state.lead);
      return {
        kind: 'text',
        title: 'Collect tour balance',
        subtitle: due ? `Outstanding: ${fmtAmt(due)}` : null,
        hint: 'Enter amount received (LKR)',
        inputType: 'number',
        placeholder: due ? String(Math.round(due)) : 'Amount',
      };
    }

    if (state.managePhase === 'edit_field' && state.editField) {
      return {
        kind: 'text',
        title: `New ${state.editField}`,
        hint: state.editField === 'note' || state.editField === 'airline' ? 'Leave blank or type skip to clear' : null,
        optional: state.editField === 'note' || state.editField === 'airline',
        inputType: 'text',
      };
    }

    if (state.managePhase === 'edit_not_interested') {
      const step = ctx.currentNotInterestedStep?.();
      if (step) {
        const question = typeof step.ask === 'function' ? step.ask(state.stageAnswers, state.lead) : step.ask;
        const def = typeof step.default === 'function' ? step.default(state.stageAnswers, state.lead) : step.default;
        return getManageStepGuiView(step, question, def);
      }
    }

    if (state.managePhase === 'call_log') {
      const step = ctx.currentCallLogStep?.();
      if (step) {
        const question = typeof step.ask === 'function' ? step.ask(state.callLogAnswers, state.lead) : step.ask;
        const def = typeof step.default === 'function'
          ? step.default(state.callLogAnswers, state.lead)
          : step.default;
        return getManageStepGuiView(step, question, def);
      }
    }

    if (state.managePhase === 'pricing_menu') {
      const targets = ctx.listPricingTargets?.(state.lead) || [];
      return {
        kind: 'choices',
        title: 'Pricing',
        subtitle: state.leadAccess?.can_edit ? 'Pick adult or amount to edit' : 'View only',
        choices: [
          ...targets.map((t, i) => ({ label: t.label, value: String(i + 1) })),
          { label: 'Done', value: 'done', muted: true },
        ],
      };
    }

    if (state.managePhase === 'pricing_field_menu' && state.pricingTarget) {
      const choices = listPricingFieldChoices(state.pricingTarget);
      return {
        kind: 'choices',
        title: `Edit ${state.pricingTarget.label}`,
        choices: [
          ...choices,
          { label: 'Back', value: 'back', muted: true },
        ],
      };
    }

    if (state.managePhase === 'pricing_value' && state.pricingTarget) {
      const prompt = ctx.pricingValuePrompt?.(
        state.pricingTarget,
        state.pricingField,
        state.lead,
      );
      return {
        kind: 'text',
        title: prompt?.question || 'New amount',
        hint: prompt?.hint || 'Amount in LKR',
        inputType: 'number',
      };
    }

    if (state.managePhase === 'edit_pax_field' && state.editPaxTarget && state.editPaxField) {
      const prompt = ctx.passengerFieldPrompt?.(state.editPaxTarget, state.editPaxField, state.lead);
      return {
        kind: 'text',
        title: prompt?.question || `New ${state.editPaxField}`,
        hint: prompt?.hint || null,
        inputType: inferInputType(state.editPaxField),
      };
    }

    if (!state.managePhase || state.managePhase === 'questions') {
      const step = ctx.currentManageStep?.();
      if (step) {
        const question = typeof step.ask === 'function' ? step.ask(state.stageAnswers, state.lead) : step.ask;
        const def = typeof step.default === 'function' ? step.default(state.stageAnswers, state.lead) : step.default;
        return getManageStepGuiView(step, question, def);
      }

      return {
        kind: 'menu',
        title: 'Manage inquiry',
        subtitle: state.lead ? statusDisplayName(state.lead.status) : null,
        actions: [
          ...(state.leadAccess?.can_edit ? [{ label: 'Edit details', value: 'edit', primary: true }] : []),
          ...(!state.leadAccess?.can_edit ? [{ label: 'Request edit access', value: 'request edit' }] : []),
          { label: 'View history', value: 'history' },
          ...(!isViewer(state.agent) ? [{ label: 'Log call', value: 'call', primary: true }] : []),
          ...(state.leadAccess?.can_edit ? [{ label: 'Pricing', value: 'pricing' }] : [{ label: 'View pricing', value: 'pricing' }]),
          ...(canDeleteLead(state.agent, state.lead, state.leadAccess)
            ? [{ label: 'Delete inquiry', value: 'delete', danger: true }]
            : []),
          ...(state.lead?.type === 'tour' && leadOutstandingBalance(state.lead) > 0
            ? [{ label: 'Collect balance', value: 'collect balance', primary: true }]
            : []),
          { label: 'Show summary', value: 'summary' },
          { label: 'Back to menu', value: 'ig' },
        ],
      };
    }
  }

  return { kind: 'idle', title: 'Assistant', subtitle: 'Use the terminal or type GUI OFF.' };
}

function inferInputType(key) {
  const k = String(key || '');
  if (k.includes('phone')) return 'tel';
  if (k.startsWith('pax_') || k.startsWith('rate_') || k === 'budget' || k === 'advance' || k.startsWith('sell_')) return 'number';
  if (k.includes('cost') || k.includes('profit') || k.includes('amount') || k.includes('payment')) return 'number';
  if (k.includes('email')) return 'email';
  if (k.includes('date')) return 'text';
  if (k.includes('time')) return 'text';
  return 'text';
}

function el(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text != null) node.textContent = text;
  return node;
}

function renderChoices(container, view, onSubmit) {
  (view.choices || []).forEach((c) => {
    const btn = el('button', `assistant-gui-btn${c.primary ? ' is-primary' : ''}${c.muted ? ' is-muted' : ''}${c.danger ? ' is-danger' : ''}`, c.label);
    btn.type = 'button';
    btn.addEventListener('click', () => onSubmit(c.value));
    container.appendChild(btn);
  });
}

function renderActions(container, actions, onSubmit) {
  (actions || []).forEach((a) => {
    const btn = el('button', `assistant-gui-btn${a.primary ? ' is-primary' : ''}`, a.label);
    btn.type = 'button';
    btn.addEventListener('click', () => onSubmit(a.value));
    container.appendChild(btn);
  });
}

function renderYn(container, view, onSubmit) {
  const row = el('div', 'assistant-gui-row');
  const yes = el('button', 'assistant-gui-btn is-primary', 'Yes');
  const no = el('button', 'assistant-gui-btn', 'No');
  yes.type = 'button';
  no.type = 'button';
  yes.addEventListener('click', () => onSubmit(view.yes || 'Y'));
  no.addEventListener('click', () => onSubmit(view.no || 'N'));
  row.append(yes, no);
  container.appendChild(row);
}

function renderTextForm(container, view, onSubmit) {
  const form = el('form', 'assistant-gui-form');
  const input = el('input', 'assistant-gui-field');
  input.type = view.inputType || 'text';
  input.placeholder = view.placeholder || '';
  input.autocomplete = 'off';
  form.appendChild(input);

  const row = el('div', 'assistant-gui-row');
  const go = el('button', 'assistant-gui-btn is-primary', 'Continue');
  go.type = 'submit';
  row.appendChild(go);
  if (view.optional) {
    const skip = el('button', 'assistant-gui-btn is-muted', 'Skip');
    skip.type = 'button';
    skip.addEventListener('click', () => onSubmit('skip'));
    row.appendChild(skip);
  }
  if (view.keepValue) {
    const keep = el('button', 'assistant-gui-btn is-muted', 'Keep current');
    keep.type = 'button';
    keep.addEventListener('click', () => onSubmit(''));
    row.appendChild(keep);
  }
  form.appendChild(row);

  form.addEventListener('submit', (e) => {
    e.preventDefault();
    onSubmit(input.value);
    input.value = '';
  });

  container.appendChild(form);
  input.focus();
}

function renderSearchForm(container, onSubmit, hint) {
  const form = el('form', 'assistant-gui-form');
  const input = el('input', 'assistant-gui-field');
  input.type = 'search';
  input.placeholder = hint || 'search /text john · /tickets /mine';
  input.autocomplete = 'off';
  const go = el('button', 'assistant-gui-btn is-primary', 'Search');
  go.type = 'submit';
  form.append(input, go);
  form.addEventListener('submit', (e) => {
    e.preventDefault();
    const q = input.value.trim();
    if (q) onSubmit(`search ${q}`);
  });
  container.appendChild(form);
}

function renderSearchResults(container, view, onSubmit) {
  (view.items || []).forEach((item) => {
    const card = el('div', `assistant-gui-card${item.selected ? ' is-selected' : ''}`);
    card.innerHTML = `<strong>${esc(item.label)}</strong><span class="assistant-gui-card-meta">${esc(item.status)}${item.due ? ` · Due ${esc(fmtAmt(item.due))}` : ''}</span>`;
    const row = el('div', 'assistant-gui-row');
    const sel = el('button', 'assistant-gui-btn', 'Select');
    const open = el('button', 'assistant-gui-btn is-primary', 'Open');
    sel.type = 'button';
    open.type = 'button';
    sel.addEventListener('click', () => onSubmit(String(item.index)));
    open.addEventListener('click', () => onSubmit(`open ${item.index}`));
    row.append(sel, open);
    card.appendChild(row);
    container.appendChild(card);
  });
}

/**
 * @param {HTMLElement} root
 * @param {object} state
 * @param {object} ctx
 * @param {(value: string) => void} onSubmit
 */
export function renderAssistantGui(root, state, ctx, onSubmit) {
  if (!root) return;
  root.innerHTML = '';
  const view = getAssistantGuiView(state, ctx);

  const head = el('div', 'assistant-gui-step-title', view.title);
  root.appendChild(head);
  if (view.subtitle) {
    root.appendChild(el('p', 'assistant-gui-step-sub', view.subtitle));
  }
  if (view.hint) {
    root.appendChild(el('p', 'assistant-gui-step-hint', view.hint));
  }

  const body = el('div', 'assistant-gui-controls');

  switch (view.kind) {
    case 'busy':
    case 'idle':
      body.appendChild(el('p', 'assistant-gui-step-hint', view.subtitle || 'Waiting for next step…'));
      break;
    case 'menu':
      if (view.search) renderSearchForm(body, onSubmit, view.searchHint);
      renderActions(body, view.actions, onSubmit);
      break;
    case 'search':
      renderSearchResults(body, view, onSubmit);
      renderActions(body, view.actions, onSubmit);
      break;
    case 'yn':
      renderYn(body, view, onSubmit);
      break;
    case 'choices':
      renderChoices(body, view, onSubmit);
      break;
    case 'text':
      renderTextForm(body, view, onSubmit);
      break;
    default:
      break;
  }

  root.appendChild(body);
}
